import React, { FC, useState } from 'react';
import styled from 'styled-components';
import _isArray from 'lodash/isArray';
import _isBoolean from 'lodash/isBoolean';
import _isEmpty from 'lodash/isEmpty';
import _isEqualWith from 'lodash/isEqualWith';
import _includes from 'lodash/includes';
import _some from 'lodash/some';
import _map from 'lodash/map';
import _reduce from 'lodash/reduce';
import _filter from 'lodash/filter';
import _omit from 'lodash/omit';
import Modal from '@minecraft.modal';
import { useTranslation } from '@blocs.i18n';
import { PreparePillsResult, isEqualCustomizer } from '../../../shared/utils';
import { ClearButton } from '../../../shared/styles';
import { Pill } from '../../../shared/components';
import { PillList } from '../FilterBasicBreadboxStyles';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledClearButton = styled(ClearButton)`
  align-self: flex-end;
  margin: 0;
`;

const FilterPillList = styled.div`
  &:not(:first-child) {
    margin-top: 1rem;
  }
`;

const FilterPillListHead = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
`;

const FilterPillListTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  margin-right: 0.5rem;
`;

type Props = {
  pills: PreparePillsResult;
  onFiltersValueClear: (selectedFiltersValues: Record<string, any>) => void;
  toggleModal: () => void;
  'data-testid'?: string;
};

const FilterBasicBreadboxModal: FC<Props> = ({ 'data-testid': testId, pills, onFiltersValueClear, toggleModal }) => {
  const pillsKeys = Object.keys(pills);
  const [selectedFiltersValues, setSelectedFiltersValues] = useState({});
  const { t } = useTranslation();

  // handler for clearing one value of a filter
  const handleFiltersValueClear = (currentFilterKey?: string, newFilterValue?: any): void => {
    if (_isArray(pills[currentFilterKey].value)) {
      const isNewFilterValueSelected = _some(selectedFiltersValues[currentFilterKey], { value: newFilterValue.value });

      setSelectedFiltersValues({
        ...selectedFiltersValues,
        [currentFilterKey]: isNewFilterValueSelected
          ? _filter(
              selectedFiltersValues[currentFilterKey],
              (selectedFiltersValue) => selectedFiltersValue !== newFilterValue
            )
          : [...(selectedFiltersValues[currentFilterKey] || []), newFilterValue],
      });

      return;
    }

    const isNewFilterSelected = _includes(Object.keys(selectedFiltersValues), currentFilterKey);
    const newSelectedFiltersValues = isNewFilterSelected
      ? _omit(selectedFiltersValues, currentFilterKey)
      : { ...selectedFiltersValues, [currentFilterKey]: null };

    setSelectedFiltersValues(newSelectedFiltersValues);
  };

  // handler for clearing all values of a filter or all filter
  const handleFiltersValuesClear = (currentFilterKey?: string): void => {
    if (currentFilterKey) {
      const areAllFilterValuesSelected =
        selectedFiltersValues[currentFilterKey]?.length === pills[currentFilterKey]?.value?.length;

      setSelectedFiltersValues({
        ...selectedFiltersValues,
        [currentFilterKey]: areAllFilterValuesSelected ? [] : pills[currentFilterKey]?.value,
      });

      return;
    }

    const appliedFiltersValues = _reduce(
      pillsKeys,
      (acc, pillKey) => {
        if (_isArray(pills[pillKey]?.value)) {
          return { ...acc, [pillKey]: pills[pillKey]?.value };
        }

        return {
          ...acc,
          [pillKey]: null,
        };
      },
      {}
    );
    const areAllFiltersValuesSelected =
      Object.keys(selectedFiltersValues).length === Object.keys(appliedFiltersValues).length &&
      _isEqualWith(selectedFiltersValues, appliedFiltersValues, isEqualCustomizer);

    setSelectedFiltersValues(areAllFiltersValuesSelected ? {} : appliedFiltersValues);
  };

  const handleFiltersValueClearSubmit = (): void => {
    if (!_isEmpty(selectedFiltersValues)) {
      onFiltersValueClear(selectedFiltersValues);
    }

    toggleModal();
  };

  const renderPillList = (pillKey: string): JSX.Element | JSX.Element[] => {
    if (_isArray(pills[pillKey].value)) {
      return _map(pills[pillKey].value, (pill) => (
        <Pill
          key={pill.value}
          isPillSelected={_includes(selectedFiltersValues[pillKey], pill)}
          pillKey={pill.value}
          text={pill.label}
          onClick={(): void => handleFiltersValueClear(pillKey, pill)}
        />
      ));
    }

    if (_isBoolean(pills[pillKey].value)) {
      return (
        <Pill
          isPillSelected={_includes(Object.keys(selectedFiltersValues), pillKey)}
          pillKey={pills[pillKey].name}
          text={pills[pillKey].name}
          onClick={(): void => handleFiltersValueClear(pillKey)}
        />
      );
    }

    return (
      <Pill
        isPillSelected={_includes(Object.keys(selectedFiltersValues), pillKey)}
        pillKey={pills[pillKey].value}
        text={pills[pillKey].value}
        onClick={(): void => handleFiltersValueClear(pillKey)}
      />
    );
  };

  return (
    <Modal
      isOpen
      title={t('common:filter.appliedFilters')}
      cancelLabel={t('common:button.cancel')}
      submitLabel={t('common:button.confirm')}
      handleToggleClick={toggleModal}
      handleSubmitClick={handleFiltersValueClearSubmit}
      data-testid={testId}
      data-qa-id={testId}
    >
      <ModalContent>
        <StyledClearButton
          role="button"
          onClick={(): void => {
            handleFiltersValuesClear();
          }}
        >
          {t('common:filter.clearAllFilters')}
        </StyledClearButton>
        {_map(pillsKeys, (key) => {
          const pillName = pills[key].name;
          const pillTitle = typeof pillName === 'string' ? pillName : pillName.label;

          return (
            <FilterPillList key={key} data-testid={`${key}-section`}>
              <FilterPillListHead>
                <FilterPillListTitle data-testid={`${key}-title`}>{pillTitle}</FilterPillListTitle>{' '}
                {_isArray(pills[key].value) && (
                  <ClearButton
                    role="button"
                    onClick={(): void => {
                      handleFiltersValuesClear(key);
                    }}
                  >
                    {t('common:filter.clearAll')}
                  </ClearButton>
                )}
              </FilterPillListHead>
              <PillList data-testid={`${key}-list`}>{renderPillList(key)}</PillList>
            </FilterPillList>
          );
        })}
      </ModalContent>
    </Modal>
  );
};

export default FilterBasicBreadboxModal;
